import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, Stepper } from '../shared';

const STORAGE_KEY = 'equipment_compare_calc_last';

// Equipment modes
const EQUIPMENT_MODES = {
  weapon: { id: 'weapon', icon: '⚔️', image: '/icons/weapon.svg' },
  armor: { id: 'armor', icon: '🛡️', image: '/icons/armor.svg' },
  jewelry: { id: 'jewelry', icon: '💍', image: '/icons/jewelry.svg' },
};

// Grade colors
const GRADE_COLORS = {
  S: '#d4af37',  // Gold
  A: '#a855f7',  // Purple
  B: '#3b82f6',  // Blue
  C: '#22c55e',  // Green
  D: '#94a3b8',  // Gray
};

// ============== WEAPON DATA ==============
// Supported formats: PNG, SVG, WebP
// Recommended size: 32x32 or 64x64 pixels
const WEAPON_TYPES = {
  mage: { id: 'mage', icon: '🪄', image: '/icons/items/weapon_arcana_mace_i01.png' },
  bow: { id: 'bow', icon: '🏹', image: '/icons/items/weapon_draconic_bow_i01.png' },
  dual: { id: 'dual', icon: '⚔️⚔️', image: '/icons/items/weapon_dual_sword_i00.png' },
  dagger: { id: 'dagger', icon: '🗡️', image: '/icons/items/weapon_angel_slayer_i01.png' },
  fist: { id: 'fist', icon: '👊', image: '/icons/items/weapon_demon_splinter_i01.png' },
  blunt: { id: 'blunt', icon: '🔨', image: '/icons/items/weapon_dragon_hunter_axe_i01.png' },
  pike: { id: 'pike', icon: '🔱', image: '/icons/items/weapon_saint_spear_i01.png' },
  sword: { id: 'sword', icon: '⚔️', image: '/icons/items/weapon_heavens_divider_i01.png' },
};

const ENCHANT_SCROLLS = {
  D: { '1H/Spear': { bonusType: 'P.Atk', bonus0_3: 2, bonus4Plus: 4 }, '2H': { bonusType: 'P.Atk', bonus0_3: 2, bonus4Plus: 4 }, Bow: { bonusType: 'P.Atk', bonus0_3: 4, bonus4Plus: 8 }, Mage: { bonusType: 'M.Atk', bonus0_3: 2, bonus4Plus: 4 } },
  C: { '1H/Spear': { bonusType: 'P.Atk', bonus0_3: 3, bonus4Plus: 6 }, '2H': { bonusType: 'P.Atk', bonus0_3: 4, bonus4Plus: 8 }, Bow: { bonusType: 'P.Atk', bonus0_3: 6, bonus4Plus: 12 }, Mage: { bonusType: 'M.Atk', bonus0_3: 3, bonus4Plus: 6 } },
  B: { '1H/Spear': { bonusType: 'P.Atk', bonus0_3: 3, bonus4Plus: 6 }, '2H': { bonusType: 'P.Atk', bonus0_3: 4, bonus4Plus: 8 }, Bow: { bonusType: 'P.Atk', bonus0_3: 6, bonus4Plus: 12 }, Mage: { bonusType: 'M.Atk', bonus0_3: 3, bonus4Plus: 6 } },
  A: { '1H/Spear': { bonusType: 'P.Atk', bonus0_3: 4, bonus4Plus: 8 }, '2H': { bonusType: 'P.Atk', bonus0_3: 5, bonus4Plus: 10 }, Bow: { bonusType: 'P.Atk', bonus0_3: 8, bonus4Plus: 16 }, Mage: { bonusType: 'M.Atk', bonus0_3: 3, bonus4Plus: 6 } },
  S: { '1H/Spear': { bonusType: 'P.Atk', bonus0_3: 5, bonus4Plus: 10 }, '2H': { bonusType: 'P.Atk', bonus0_3: 6, bonus4Plus: 12 }, Bow: { bonusType: 'P.Atk', bonus0_3: 10, bonus4Plus: 20 }, Mage: { bonusType: 'M.Atk', bonus0_3: 4, bonus4Plus: 8 } },
};

const WEAPON_SCROLL_TYPE = {
  sword: '1H/Spear', blunt: '1H/Spear', dagger: '1H/Spear', pike: '1H/Spear',
  fist: '1H/Spear', dual: '1H/Spear', bow: 'Bow', mage: 'Mage',
};

const WEAPONS_BY_TYPE = {
  sword: [
    { name: "Heaven's Divider", base: 342, grade: 'S' },
    { name: 'Forgotten Blade', base: 281, grade: 'S' },
    { name: 'Sword of Ipos', base: 305, grade: 'A' },
    { name: 'Dragon Slayer', base: 282, grade: 'A' },
    { name: 'Infernal Master', base: 259, grade: 'A' },
    { name: "Sirra's Blade", base: 251, grade: 'A' },
    { name: "Dark Legion's Edge", base: 232, grade: 'A' },
    { name: 'Tallum Blade', base: 213, grade: 'A' },
    { name: 'Sword of Damascus', base: 194, grade: 'B' },
    { name: 'Keshanberk', base: 175, grade: 'B' },
    { name: 'Great Sword', base: 213, grade: 'B' },
  ],
  blunt: [
    { name: 'Dragon Hunter Axe', base: 342, grade: 'S' },
    { name: 'Basalt Battlehammer', base: 281, grade: 'S' },
    { name: "Behemoth's Tuning Fork", base: 305, grade: 'A' },
    { name: 'Doom Crusher', base: 282, grade: 'A' },
    { name: "Barakiel's Axe", base: 251, grade: 'A' },
    { name: 'Elysian', base: 232, grade: 'A' },
    { name: 'Star Buster', base: 236, grade: 'B' },
    { name: 'Ice Storm Hammer', base: 213, grade: 'B' },
    { name: 'Art of Battle Axe', base: 194, grade: 'B' },
    { name: 'Heavy War Axe', base: 175, grade: 'B' },
  ],
  dagger: [
    { name: 'Angel Slayer', base: 246, grade: 'S' },
    { name: 'Naga Storm', base: 220, grade: 'A' },
    { name: 'Soul Separator', base: 203, grade: 'A' },
    { name: 'Bloody Orchid', base: 186, grade: 'A' },
    { name: 'Demon Dagger', base: 170, grade: 'B' },
    { name: 'Kris', base: 153, grade: 'B' },
  ],
  pike: [
    { name: 'Saint Spear', base: 281, grade: 'S' },
    { name: "Tiphon's Spear", base: 251, grade: 'A' },
    { name: 'Tallum Glaive', base: 232, grade: 'A' },
    { name: 'Orcish Halberd', base: 219, grade: 'A' },
    { name: 'Halberd', base: 213, grade: 'A' },
    { name: 'Lance', base: 194, grade: 'B' },
    { name: 'Great Axe', base: 175, grade: 'B' },
  ],
  fist: [
    { name: 'Demon Splinter', base: 342, grade: 'S' },
    { name: "Sobekk's Hurricane", base: 305, grade: 'A' },
    { name: 'Dragon Grinder', base: 282, grade: 'A' },
    { name: 'Blood Tornado', base: 259, grade: 'A' },
    { name: 'Bellion Cestus', base: 236, grade: 'B' },
    { name: 'Arthro Nail', base: 213, grade: 'B' },
  ],
  dual: [
    { name: "Tallum*Dark Legion's", base: 342, grade: 'S' },
    { name: 'Damascus*Tallum', base: 305, grade: 'A' },
    { name: 'Damascus*Damascus', base: 282, grade: 'A' },
    { name: 'Keshanberk*Damascus', base: 275, grade: 'A' },
    { name: 'Keshanberk*Keshanberk', base: 259, grade: 'A' },
    { name: 'SLS*SLS', base: 236, grade: 'B' },
    { name: 'Tsurugi*Tsurugi', base: 213, grade: 'B' },
  ],
  bow: [
    { name: 'Draconic Bow', base: 581, grade: 'S' },
    { name: "Shyeed's Bow", base: 570, grade: 'A' },
    { name: 'Soul Bow', base: 528, grade: 'A' },
    { name: 'Carnage Bow', base: 440, grade: 'A' },
    { name: 'Bow of Peril', base: 400, grade: 'B' },
    { name: 'Dark Elven Long Bow', base: 397, grade: 'B' },
  ],
  mage: [
    { name: 'Imperial Staff', base: 183, grade: 'S' },
    { name: 'Arcana Mace', base: 175, grade: 'S' },
    { name: 'Daimon Crystal', base: 167, grade: 'A' },
    { name: "Cabrio's Hand", base: 161, grade: 'A' },
    { name: 'Sword of Miracles', base: 152, grade: 'A' },
    { name: 'Spiritual Eye', base: 143, grade: 'A' },
    { name: "Dasparion's Staff", base: 149, grade: 'A' },
    { name: "Wizard's Tear", base: 132, grade: 'B' },
    { name: 'Sword of Valhalla', base: 122, grade: 'B' },
  ],
};

// ============== ARMOR DATA ==============
const ARMOR_TYPES = {
  heavy: { id: 'heavy', icon: '🛡️', name: 'Heavy', image: '/icons/items/armor_t88_u_i00.png' },
  light: { id: 'light', icon: '🥋', name: 'Light', image: '/icons/items/armor_t89_ul_i00.png' },
  robe: { id: 'robe', icon: '👘', name: 'Robe', image: '/icons/items/armor_t90_ul_i00.png' },
};

// Armor enchant bonus per piece per grade
const ARMOR_ENCHANT = {
  S: { bonus0_3: 1, bonus4Plus: 3 },
  A: { bonus0_3: 1, bonus4Plus: 3 },
  B: { bonus0_3: 1, bonus4Plus: 3 },
};

// Armor sets: pDef is total set P.Def, mDef is total set M.Def
// Pieces count for enchant calculation (usually 5-6 pieces)
const ARMOR_SETS = {
  heavy: [
    //S
    { name: 'Imperial Crusader', pDef: 526, mDef: 0, grade: 'S', pieces: 5 },
    //A
    { name: 'Majestic Heavy', pDef: 464, mDef: 0, grade: 'A', pieces: 4 },
    { name: 'Tallum Heavy', pDef: 439, mDef: 0, grade: 'A', pieces: 4 },
    { name: 'Nightmare Heavy', pDef: 464, mDef: 0, grade: 'A', pieces: 4 },
    { name: 'Dark Crystal Heavy', pDef: 439, mDef: 0, grade: 'A', pieces: 5 },
    //B
    { name: 'Blue Wolf Heavy', pDef: 424, mDef: 0, grade: 'B', pieces: 5 },
    { name: 'Doom Heavy', pDef: 424, mDef: 0, grade: 'B', pieces: 4 },
    { name: 'Avadon Heavy', pDef: 399, mDef: 0, grade: 'B', pieces: 5 },
    { name: 'Zubei Heavy', pDef: 399, mDef: 0, grade: 'B', pieces: 5 },
  ],
  light: [
    //S
    { name: 'Draconic Leather', pDef: 442, mDef: 0, grade: 'S', pieces: 4 },
    //A
    { name: 'Majestic Light', pDef: 391, mDef: 0, grade: 'A', pieces: 4 },
    { name: 'Nightmare Light', pDef: 391, mDef: 0, grade: 'A', pieces: 4 },
    { name: 'Tallum Light', pDef: 370, mDef: 0, grade: 'A', pieces: 4 },
    { name: 'Dark Crystal Light', pDef: 369, mDef: 0, grade: 'A', pieces: 5 },
    //B
    { name: 'Blue Wolf Light', pDef: 356, mDef: 0, grade: 'B', pieces: 4 },
    { name: 'Doom Light', pDef: 356, mDef: 0, grade: 'B', pieces: 4 },
    { name: 'Avadon Light', pDef: 335, mDef: 0, grade: 'B', pieces: 4 },
    { name: 'Zubei Light', pDef: 334, mDef: 0, grade: 'B', pieces: 5 },
  ],
  robe: [
    //S
    { name: 'Major Arcana', pDef: 359, mDef: 0, grade: 'S', pieces: 4 },
    //A
    { name: 'Majestic Robe', pDef: 318, mDef: 0, grade: 'A', pieces: 4 },
    { name: 'Nightmare Robe', pDef: 318, mDef: 0, grade: 'A', pieces: 4 },
    { name: 'Tallum Robe', pDef: 300, mDef: 0, grade: 'A', pieces: 5 },
    { name: 'Dark Crystal Robe', pDef: 300, mDef: 0, grade: 'A', pieces: 4 },
    //B
    { name: 'Blue Wolf Robe', pDef: 289, mDef: 0, grade: 'B', pieces: 5 },
    { name: 'Doom Robe', pDef: 289, mDef: 0, grade: 'B', pieces: 5 },
    { name: 'Avadon Robe', pDef: 271, mDef: 0, grade: 'B', pieces: 4 },
    { name: 'Zubei Robe', pDef: 271, mDef: 0, grade: 'B', pieces: 5 },
  ],
};

// ============== JEWELRY DATA ==============
const JEWELRY_MODES = {
  individual: { id: 'individual', name: 'Individual' },
  sets: { id: 'sets', name: 'Sets' },
};

const JEWELRY_PIECE_TYPES = {
  necklace: { id: 'necklace', icon: '📿', name: 'Necklace', image: '/icons/items/accessory_necklace_of_valakas_i00.png' },
  earring: { id: 'earring', icon: '💎', name: 'Earring', image: '/icons/items/accessory_earring_of_antaras_i00.png' },
  ring: { id: 'ring', icon: '💍', name: 'Ring', image: '/icons/items/accessory_ring_of_baium_i00.png' },
};

// Jewelry enchant bonus (M.Def)
const JEWELRY_ENCHANT = {
  S: { bonus0_3: 1, bonus4Plus: 3 },
  A: { bonus0_3: 1, bonus4Plus: 3 },
  B: { bonus0_3: 1, bonus4Plus: 3 },
  epic: { bonus0_3: 1, bonus4Plus: 3 }, // Epic jewelry is enchantable
};

// Jewelry sets (full set: 1 necklace, 2 earrings, 2 rings)
const JEWELRY_SETS = [
  { name: 'Epic / Tateossian', mDef: 333, grade: 'S', pieces: 5 },
  { name: 'Majestic', mDef: 295, grade: 'A', pieces: 5 },
  { name: 'Phoenix', mDef: 280, grade: 'A', pieces: 5 },
  { name: 'Black Ore', mDef: 252, grade: 'B', pieces: 5 },
];

//TODO: grade should apply to enchants, isEpic should be for showing "epic" badge maybe?
// Individual jewelry pieces
const JEWELRY_PIECES = {
  necklace: [
    { name: 'Necklace of Valakas', mDef: 95, grade: 'S', isEpic: true },
    { name: 'Frintezza\'s Necklace', mDef: 95, grade: 'A', isEpic: true },
    { name: 'Tateossian Necklace', mDef: 95, grade: 'S' },
    { name: 'Majestic Necklace', mDef: 85, grade: 'A' },
    { name: 'Phoenix Necklace', mDef: 80, grade: 'A' },
    { name: 'Necklace of Black Ore', mDef: 72, grade: 'B' },
  ],
  earring: [
    { name: 'Earring of Antharas', mDef: 71, grade: 'S', isEpic: true },
    { name: 'Zaken\'s Earring', mDef: 71, grade: 'S', isEpic: true },
    { name: 'Earring of Orfen', mDef: 71, grade: 'A', isEpic: true },
    { name: 'Tateossian Earring', mDef: 71, grade: 'S' },
    { name: 'Majestic Earring', mDef: 63, grade: 'A' },
    { name: 'Phoenix Earring', mDef: 60, grade: 'A' },
    { name: 'Earring of Black Ore', mDef: 36, grade: 'B' },
  ],
  ring: [
    { name: 'Ring of Baium', mDef: 48, grade: 'S', isEpic: true },
    { name: 'Ring of Core', mDef: 48, grade: 'A', isEpic: true },
    { name: 'Ring of Queen Ant', mDef: 48, grade: 'B', isEpic: true },
    { name: 'Tateossian Ring', mDef: 48, grade: 'S' },
    { name: 'Majestic Ring', mDef: 42, grade: 'A' },
    { name: 'Phoenix Ring', mDef: 40, grade: 'A' },
    { name: 'Ring of Black Ore', mDef: 36, grade: 'B' },
  ],
};

// ============== CALCULATION FUNCTIONS ==============

function getWeaponEnchantData(weaponType, grade) {
  const scrollType = WEAPON_SCROLL_TYPE[weaponType];
  return ENCHANT_SCROLLS[grade]?.[scrollType] || null;
}

function calculateWeaponAttack(base, enchantLevel, weaponType, grade) {
  const enchantData = getWeaponEnchantData(weaponType, grade);
  if (!enchantData || enchantLevel <= 0) return base;

  let attack = base;
  for (let i = 1; i <= enchantLevel; i++) {
    attack += i <= 3 ? enchantData.bonus0_3 : enchantData.bonus4Plus;
  }
  return attack;
}

function calculateArmorDef(baseDef, enchantLevel, grade, pieces = 5) {
  const enchantData = ARMOR_ENCHANT[grade];
  if (!enchantData || enchantLevel <= 0) return baseDef;

  let bonus = 0;
  for (let i = 1; i <= enchantLevel; i++) {
    bonus += (i <= 3 ? enchantData.bonus0_3 : enchantData.bonus4Plus) * pieces;
  }
  return baseDef + bonus;
}

function calculateJewelryMDef(baseMDef, enchantLevel, grade, pieces = 1) {
  const enchantData = JEWELRY_ENCHANT[grade];
  if (!enchantData || enchantLevel <= 0) return baseMDef;

  let bonus = 0;
  for (let i = 1; i <= enchantLevel; i++) {
    bonus += (i <= 3 ? enchantData.bonus0_3 : enchantData.bonus4Plus) * pieces;
  }
  return baseMDef + bonus;
}

function findCrossoverLevel(currentStat, compareFn, maxLevel = 16) {
  for (let lvl = 0; lvl <= maxLevel; lvl++) {
    const compareStat = compareFn(lvl);
    if (compareStat >= currentStat) {
      return { level: lvl, stat: compareStat };
    }
  }
  return null;
}

// ============== ICON COMPONENT WITH FALLBACK ==============
// This component tries to load the image, falls back to emoji if image fails
// SVG images use CSS mask technique for color control, PNGs use regular <img>
function TypeIcon({ type, className, size = 'normal', framed = true }) {
  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const isSvg = type.image && type.image.endsWith('.svg');

  // Reset error state when type changes
  React.useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [type.image]);

  // If no image path or image failed to load, show emoji
  if (!type.image || imageError) {
    return <span className={className}>{type.icon}</span>;
  }

  // SVG icons use mask-image for CSS color control (same as menu icons)
  if (isSvg) {
    return (
      <span
        className={`${className} type-icon-svg`}
        style={{
          maskImage: `url(${type.image})`,
          WebkitMaskImage: `url(${type.image})`,
        }}
      />
    );
  }

  return (
    <>
      {!imageLoaded && <span className={className}>{type.icon}</span>}
      <img
        src={type.image}
        alt={type.id}
        className={`${className} type-icon-img ${size === 'large' ? 'type-icon-img-large' : ''} ${framed ? 'type-icon-img-framed' : ''}`}
        style={{ display: imageLoaded ? 'block' : 'none' }}
        onError={() => setImageError(true)}
        onLoad={() => setImageLoaded(true)}
      />
    </>
  );
}

// ============== COMPONENT ==============

export function EquipmentPanel({ active }) {
  const { t } = useLanguage();

  // Mode state
  const [equipmentMode, setEquipmentMode] = useState('weapon');
  
  // Weapon state
  const [weaponType, setWeaponType] = useState('mage');
  const [currentWeaponIdx, setCurrentWeaponIdx] = useState(1);
  const [currentWeaponEnchant, setCurrentWeaponEnchant] = useState(6);

  // Armor state
  const [armorType, setArmorType] = useState('heavy');
  const [currentArmorIdx, setCurrentArmorIdx] = useState(0);
  const [currentArmorEnchant, setCurrentArmorEnchant] = useState(0);

  // Jewelry state
  const [jewelryMode, setJewelryMode] = useState('individual');
  const [jewelryPieceType, setJewelryPieceType] = useState('necklace');
  const [currentJewelrySetIdx, setCurrentJewelrySetIdx] = useState(0);
  const [currentJewelryPieceIdx, setCurrentJewelryPieceIdx] = useState(0);
  const [currentJewelryEnchant, setCurrentJewelryEnchant] = useState(0);

  // Breakdown table visibility states
  const [showWeaponBreakdown, setShowWeaponBreakdown] = useState(false);
  const [showArmorBreakdown, setShowArmorBreakdown] = useState(false);
  const [showJewelryBreakdown, setShowJewelryBreakdown] = useState(false);

  // Expanded comparison row states (tracks which item is expanded)
  const [expandedWeapon, setExpandedWeapon] = useState(null);
  const [expandedArmor, setExpandedArmor] = useState(null);
  const [expandedJewelry, setExpandedJewelry] = useState(null);

  // Track first mount to skip reset effects during restore
  const isFirstMountRef = useRef(true);

  // Restore state
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        if (state.equipmentMode) setEquipmentMode(state.equipmentMode);
        if (state.weaponType) setWeaponType(state.weaponType);
        if (state.currentWeaponIdx !== undefined) setCurrentWeaponIdx(state.currentWeaponIdx);
        if (state.currentWeaponEnchant !== undefined) setCurrentWeaponEnchant(state.currentWeaponEnchant);
        if (state.armorType) setArmorType(state.armorType);
        if (state.currentArmorIdx !== undefined) setCurrentArmorIdx(state.currentArmorIdx);
        if (state.currentArmorEnchant !== undefined) setCurrentArmorEnchant(state.currentArmorEnchant);
        if (state.jewelryMode) setJewelryMode(state.jewelryMode);
        if (state.jewelryPieceType) setJewelryPieceType(state.jewelryPieceType);
        if (state.currentJewelrySetIdx !== undefined) setCurrentJewelrySetIdx(state.currentJewelrySetIdx);
        if (state.currentJewelryPieceIdx !== undefined) setCurrentJewelryPieceIdx(state.currentJewelryPieceIdx);
        if (state.currentJewelryEnchant !== undefined) setCurrentJewelryEnchant(state.currentJewelryEnchant);
      }
    } catch (e) {}
  }, []);

  // Save state
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        equipmentMode, weaponType, currentWeaponIdx, currentWeaponEnchant,
        armorType, currentArmorIdx, currentArmorEnchant,
        jewelryMode, jewelryPieceType, currentJewelrySetIdx, currentJewelryPieceIdx, currentJewelryEnchant
      }));
    } catch (e) {}
  }, [equipmentMode, weaponType, currentWeaponIdx, currentWeaponEnchant,
      armorType, currentArmorIdx, currentArmorEnchant,
      jewelryMode, jewelryPieceType, currentJewelrySetIdx, currentJewelryPieceIdx, currentJewelryEnchant]);

  // Reset indices when type changes (skip on first mount to preserve restored values)
  useEffect(() => {
    if (isFirstMountRef.current) return;
    const weapons = WEAPONS_BY_TYPE[weaponType];
    if (weapons) setCurrentWeaponIdx(Math.min(currentWeaponIdx, weapons.length - 1));
  }, [weaponType]);

  useEffect(() => {
    if (isFirstMountRef.current) return;
    const armors = ARMOR_SETS[armorType];
    if (armors) setCurrentArmorIdx(Math.min(currentArmorIdx, armors.length - 1));
  }, [armorType]);

  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    const pieces = JEWELRY_PIECES[jewelryPieceType];
    if (pieces) setCurrentJewelryPieceIdx(Math.min(currentJewelryPieceIdx, pieces.length - 1));
  }, [jewelryPieceType]);

  // ============== WEAPON CALCULATIONS ==============
  const weapons = WEAPONS_BY_TYPE[weaponType] || [];
  const currentWeapon = weapons[currentWeaponIdx];
  const currentWeaponEnchantData = getWeaponEnchantData(weaponType, currentWeapon?.grade);
  const weaponAttackLabel = currentWeaponEnchantData?.bonusType === 'M.Atk' ? 'M.Atk' : 'P.Atk';

  const currentWeaponAttack = useMemo(() => {
    if (!currentWeapon) return 0;
    return calculateWeaponAttack(currentWeapon.base, currentWeaponEnchant, weaponType, currentWeapon.grade);
  }, [currentWeapon, currentWeaponEnchant, weaponType]);

  const weaponComparisons = useMemo(() => {
    if (!currentWeapon) return [];
    return weapons.map((weapon) => {
      const crossover = findCrossoverLevel(currentWeaponAttack, (lvl) =>
        calculateWeaponAttack(weapon.base, lvl, weaponType, weapon.grade)
      );

      const statAtCrossover = crossover 
        ? calculateWeaponAttack(weapon.base, crossover.level, weaponType, weapon.grade)
        : null;
      const diff = statAtCrossover !== null ? statAtCrossover - currentWeaponAttack : null;
      
      return {
        item: weapon,
        crossover,
        statAtCrossover,
        diff,
        isCurrent: weapon === currentWeapon,
      };
    });
  }, [weapons, currentWeapon, currentWeaponAttack, currentWeaponEnchant, weaponType]);

  // Weapon enchant breakdown table (0 to 16)
  const weaponEnchantBreakdown = useMemo(() => {
    if (!currentWeapon) return [];
    const levels = [];
    for (let lvl = 0; lvl <= 16; lvl++) {
      const row = {
        level: lvl,
        items: weapons.map((weapon) => ({
          weapon,
          stat: calculateWeaponAttack(weapon.base, lvl, weaponType, weapon.grade),
          isCurrent: weapon === currentWeapon,
        }))
      };
      levels.push(row);
    }
    return levels;
  }, [weapons, currentWeapon, weaponType]);

  // ============== ARMOR CALCULATIONS ==============
  const armors = ARMOR_SETS[armorType] || [];
  const currentArmor = armors[currentArmorIdx];

  const currentArmorPDef = useMemo(() => {
    if (!currentArmor) return 0;
    return calculateArmorDef(currentArmor.pDef, currentArmorEnchant, currentArmor.grade, currentArmor.pieces);
  }, [currentArmor, currentArmorEnchant]);

  const armorComparisons = useMemo(() => {
    if (!currentArmor) return [];
    return armors.map((armor) => {
      const crossover = findCrossoverLevel(currentArmorPDef, (lvl) =>
        calculateArmorDef(armor.pDef, lvl, armor.grade, armor.pieces)
      );
      const statAtCrossover = crossover
        ? calculateArmorDef(armor.pDef, crossover.level, armor.grade, armor.pieces)
        : null;
      const diff = statAtCrossover !== null ? statAtCrossover - currentArmorPDef : null;
      
      return {
        item: armor,
        crossover,
        statAtCrossover,
        diff,
        isCurrent: armor === currentArmor,
      };
    });
  }, [armors, currentArmor, currentArmorPDef, currentArmorEnchant]);

  // Armor enchant breakdown table (0 to 16)
  const armorEnchantBreakdown = useMemo(() => {
    if (!currentArmor) return [];
    const levels = [];
    for (let lvl = 0; lvl <= 16; lvl++) {
      const row = {
        level: lvl,
        items: armors.map((armor) => ({
          armor,
          stat: calculateArmorDef(armor.pDef, lvl, armor.grade, armor.pieces),
          isCurrent: armor === currentArmor,
        }))
      };
      levels.push(row);
    }
    return levels;
  }, [armors, currentArmor]);

  // ============== JEWELRY CALCULATIONS ==============
  const jewelrySets = JEWELRY_SETS;
  const jewelryPieces = JEWELRY_PIECES[jewelryPieceType] || [];
  
  const currentJewelrySet = jewelrySets[currentJewelrySetIdx];
  const currentJewelryPiece = jewelryPieces[currentJewelryPieceIdx];

  const currentJewelryMDef = useMemo(() => {
    if (jewelryMode === 'sets') {
      if (!currentJewelrySet) return 0;
      return calculateJewelryMDef(currentJewelrySet.mDef, currentJewelryEnchant, currentJewelrySet.grade, currentJewelrySet.pieces);
    } else {
      if (!currentJewelryPiece) return 0;
      return calculateJewelryMDef(currentJewelryPiece.mDef, currentJewelryEnchant, currentJewelryPiece.grade, 1);
    }
  }, [jewelryMode, currentJewelrySet, currentJewelryPiece, currentJewelryEnchant]);

  const jewelryComparisons = useMemo(() => {
    if (jewelryMode === 'sets') {
      if (!currentJewelrySet) return [];
      return jewelrySets.map((set) => {
        const crossover = findCrossoverLevel(currentJewelryMDef, (lvl) =>
          calculateJewelryMDef(set.mDef, lvl, set.grade, set.pieces)
        );
        const statAtCrossover = crossover
          ? calculateJewelryMDef(set.mDef, crossover.level, set.grade, set.pieces)
          : null;
        const diff = statAtCrossover !== null ? statAtCrossover - currentJewelryMDef : null;
        
        return {
          item: set,
          crossover,
          statAtCrossover,
          diff,
          isCurrent: set === currentJewelrySet,
        };
      });
    } else {
      if (!currentJewelryPiece) return [];
      return jewelryPieces.map((piece) => {
        const crossover = findCrossoverLevel(currentJewelryMDef, (lvl) =>
          calculateJewelryMDef(piece.mDef, lvl, piece.grade, 1)
        );
        const statAtCrossover = crossover
          ? calculateJewelryMDef(piece.mDef, crossover.level, piece.grade, 1)
          : null;
        const diff = statAtCrossover !== null ? statAtCrossover - currentJewelryMDef : null;
        
        return {
          item: piece,
          crossover,
          statAtCrossover,
          diff,
          isCurrent: piece === currentJewelryPiece,
        };
      });
    }
  }, [jewelryMode, jewelrySets, jewelryPieces, currentJewelrySet, currentJewelryPiece, currentJewelryMDef, currentJewelryEnchant]);

  // Jewelry enchant breakdown table (0 to 16)
  const jewelryEnchantBreakdown = useMemo(() => {
    if (jewelryMode === 'sets') {
      if (!currentJewelrySet) return [];
      const levels = [];
      for (let lvl = 0; lvl <= 16; lvl++) {
        const row = {
          level: lvl,
          items: jewelrySets.map((set) => ({
            item: set,
            stat: calculateJewelryMDef(set.mDef, lvl, set.grade, set.pieces),
            isCurrent: set === currentJewelrySet,
          }))
        };
        levels.push(row);
      }
      return levels;
    } else {
      if (!currentJewelryPiece) return [];
      const levels = [];
      for (let lvl = 0; lvl <= 16; lvl++) {
        const row = {
          level: lvl,
          items: jewelryPieces.map((piece) => ({
            item: piece,
            stat: calculateJewelryMDef(piece.mDef, lvl, piece.grade, 1),
            isCurrent: piece === currentJewelryPiece,
          }))
        };
        levels.push(row);
      }
      return levels;
    }
  }, [jewelryMode, jewelrySets, jewelryPieces, currentJewelrySet, currentJewelryPiece]);

  // ============== RENDER HELPERS ==============
  
  const renderGradeBadge = (grade) => (
    <span 
      className="grade-badge" 
      style={{ 
        border: '1px solid ' + (grade === 'epic' ? 'linear-gradient(135deg, #ff6b6b, #ffd93d)' : GRADE_COLORS[grade]),
        color: grade === 'epic' ? GRADE_COLORS[S] : GRADE_COLORS[grade]
      }}
    >
      {grade === 'epic' ? '★' : grade}
    </span>
  );

  const renderComparisonRow = (comp, statLabel, currentEnchant, currentStat, options = {}) => {
    const { item, crossover, diff, isCurrent, statAtCrossover } = comp;
    const {
      expandedItem,
      setExpandedItem,
      calculateStat,
      currentItem
    } = options;

    const diffClass = diff !== null ? (diff > 0 ? 'above' : diff < 0 ? 'below' : '') : '';
    const isExpanded = expandedItem === item.name;

    // Determine if this row represents the current item - use both isCurrent flag
    // and direct comparison to ensure it works for all equipment types
    const isCurrentItem = isCurrent || item === currentItem;

    // Determine the enchant level to display
    const displayLevel = isCurrentItem
      ? `+${currentEnchant}`
      : crossover
        ? `+${crossover.level}`
        : '—';

    // For current item, show current stat; for others show stat at crossover (or — if no crossover)
    const displayStat = isCurrentItem
      ? currentStat
      : statAtCrossover !== null
        ? statAtCrossover
        : '—';

    const handleClick = () => {
      setExpandedItem(isExpanded ? null : item.name);
    };

    return (
      <div key={item.name} className={`equipment-row-wrapper ${isExpanded ? 'expanded' : ''}`}>
        <div
          className={`equipment-row ${diffClass} ${isCurrentItem ? 'current' : ''} clickable`}
          onClick={handleClick}
        >
          <div className="equipment-row-content">
            {renderGradeBadge(item.grade)}
            <span className="equipment-level">{displayLevel}</span>
            <span className="equipment-name">{item.name}</span>
            <span className="equipment-stat">{displayStat}</span>
            {!isCurrentItem && diff !== null && diff !== 0 && (
              <span className={`equipment-diff ${diffClass}`}>
                {diff > 0 ? '↑' : '↓'}{Math.abs(diff)}
              </span>
            )}
            {!isCurrentItem && diff === 0 && (
              <span className="equipment-diff equal">=</span>
            )}
            <span className={`expand-indicator ${isExpanded ? 'open' : ''}`}>▾</span>
          </div>
        </div>

        {/* Expanded comparison: show all enchant levels of selected item vs current */}
        {isExpanded && calculateStat && currentItem && (
          <div
            className="expanded-comparison"
            ref={(el) => {
              if (el) {
                // Wait for the CSS animation to complete before scrolling
                const handleAnimationEnd = () => {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  el.removeEventListener('animationend', handleAnimationEnd);
                };
                el.addEventListener('animationend', handleAnimationEnd);
              }
            }}
          >
            <div className="expanded-comparison-header">
              <span className="expanded-col level-col">+</span>
              <span className="expanded-col theirs-col">{item.name}</span>
              <span className="expanded-col diff-col">Δ</span>
            </div>
            <div
              className="expanded-comparison-body"
              ref={(container) => {
                if (container) {
                  // Wait for parent animation to complete before scrolling table
                  const parent = container.closest('.expanded-comparison');
                  if (parent) {
                    const handleAnimationEnd = () => {
                      requestAnimationFrame(() => {
                        // Find the highlighted row (crossover or current enchant)
                        const targetRow = container.querySelector('.highlight-crossover') || container.querySelector('.highlight-yours');
                        if (targetRow) {
                          // Use getBoundingClientRect for accurate position calculation
                          const containerRect = container.getBoundingClientRect();
                          const rowRect = targetRow.getBoundingClientRect();
                          const containerHeight = container.clientHeight;
                          const rowHeight = rowRect.height;
                          // Calculate row's position within scrollable content
                          const rowTopInContainer = rowRect.top - containerRect.top + container.scrollTop;
                          // Center the row in the container
                          const scrollPosition = rowTopInContainer - (containerHeight / 2) + (rowHeight / 2);
                          container.scrollTop = Math.max(0, scrollPosition);
                        }
                      });
                      parent.removeEventListener('animationend', handleAnimationEnd);
                    };
                    parent.addEventListener('animationend', handleAnimationEnd);
                  }
                }
              }}
            >
              {Array.from({ length: 17 }, (_, lvl) => {
                const theirStat = calculateStat(item, lvl);
                const statDiff = theirStat - currentStat;
                const rowDiffClass = statDiff > 0 ? 'above' : statDiff < 0 ? 'below' : 'equal';
                const isCurrentEnchantRow = isCurrentItem && lvl === currentEnchant;
                const isCrossoverRow = !isCurrentItem && crossover && lvl === crossover.level;
                return (
                  <div
                    key={lvl}
                    className={`expanded-row ${isCurrentEnchantRow ? 'highlight-yours' : ''} ${isCrossoverRow ? 'highlight-crossover' : ''}`}
                  >
                    <span className="expanded-col level-col">+{lvl}</span>
                    <span className={`expanded-col theirs-col ${rowDiffClass}`}>{theirStat}</span>
                    <span className={`expanded-col diff-col ${rowDiffClass}`}>
                      {statDiff > 0 ? '+' : ''}{statDiff}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!active) return null;

  return (
    <div className="calculator-panel active">
      {/* Equipment Mode Switcher */}
      <div className="equipment-mode-switcher">
        {Object.entries(EQUIPMENT_MODES).map(([key, mode]) => (
          <button
            key={key}
            className={`mode-btn ${equipmentMode === key ? 'active' : ''}`}
            onClick={() => setEquipmentMode(key)}
          >
            <TypeIcon type={mode} className="mode-icon" framed={false} />
            <span className="mode-label">{t(`equip_${key}`) || key}</span>
          </button>
        ))}
      </div>

      {/* ============== WEAPON MODE ============== */}
      {equipmentMode === 'weapon' && (
        <>
          <Card title={t('weaponType') || 'WEAPON TYPE'}>
            <div className="weapon-type-grid">
              {Object.entries(WEAPON_TYPES).map(([key, type]) => (
                <button
                  key={key}
                  className={`weapon-type-btn ${weaponType === key ? 'active' : ''}`}
                  onClick={() => { setWeaponType(key); setCurrentWeaponIdx(0); }}
                >
                  <TypeIcon type={type} className="weapon-type-icon" />
                  <span className="weapon-type-name">{t(`weapon_${key}`) || key}</span>
                </button>
              ))}
            </div>
          </Card>

          <Card title={t('myWeapon') || 'MY WEAPON'}>
            <div className="equipment-selector">
              <select 
                className="equipment-dropdown"
                value={currentWeaponIdx}
                onChange={(e) => setCurrentWeaponIdx(parseInt(e.target.value))}
              >
                {weapons.map((w, idx) => (
                  <option key={idx} value={idx}>
                    [{w.grade}] {w.name} ({w.base})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="enchant-row">
              <span className="enchant-label">{t('enchantLevel') || 'Enchant'}</span>
              <Stepper value={currentWeaponEnchant} onChange={setCurrentWeaponEnchant} min={0} max={16} small />
            </div>
            
            <div className="stat-display">
              <span className="stat-label">{weaponAttackLabel}</span>
              <span className="stat-value">
                {currentWeaponAttack}
                {currentWeaponEnchant > 0 && <span className="enchant-tag">+{currentWeaponEnchant}</span>}
              </span>
            </div>
          </Card>

          <Card title={t('compareTo') || 'COMPARES TO'}>
            <div className="equipment-list">
              {weaponComparisons.map((comp) => renderComparisonRow(comp, weaponAttackLabel, currentWeaponEnchant, currentWeaponAttack, {
                expandedItem: expandedWeapon,
                setExpandedItem: setExpandedWeapon,
                calculateStat: (weapon, lvl) => calculateWeaponAttack(weapon.base, lvl, weaponType, weapon.grade),
                currentItem: currentWeapon
              }))}
            </div>
            
            <div
              className="enchant-breakdown-toggle"
              onClick={() => setShowWeaponBreakdown(!showWeaponBreakdown)}
            >
              {showWeaponBreakdown ? (t('hideBreakdown') || 'Hide breakdown') : (t('viewAllLevels') || 'View all enchant levels')}
            </div>
            
            {showWeaponBreakdown && (() => {
              // Reorder: current weapon first, then others
              const orderedWeapons = [
                currentWeapon,
                ...weapons.filter(w => w !== currentWeapon)
              ];
              const orderedBreakdown = weaponEnchantBreakdown.map(row => ({
                ...row,
                items: [
                  row.items.find(i => i.isCurrent),
                  ...row.items.filter(i => !i.isCurrent)
                ]
              }));
              
              return (
                <div className="enchant-breakdown-scroll">
                  <div className="enchant-breakdown-scroll-x">
                    <div className="enchant-breakdown-table">
                      <div className="breakdown-header">
                        <span className="breakdown-level-col sticky-col">+</span>
                        {orderedWeapons.map((w, idx) => (
                          <span key={idx} className={`breakdown-item-col ${w === currentWeapon ? 'current sticky-current' : ''}`}>
                            <span className={`grade-mini grade-${w.grade}`}>{w.grade}</span>
                            <span className="breakdown-item-name">{w.name}</span>
                          </span>
                        ))}
                      </div>
                      <div className="breakdown-body">
                        {orderedBreakdown.map((row) => (
                          <div 
                            key={row.level} 
                            className={`breakdown-row ${row.level === currentWeaponEnchant ? 'highlight' : ''}`}
                          >
                            <span className="breakdown-level-col sticky-col">+{row.level}</span>
                            {row.items.map((item, idx) => (
                              <span 
                                key={idx} 
                                className={`breakdown-item-col ${item.isCurrent ? 'current sticky-current' : ''} ${item.stat >= currentWeaponAttack && !item.isCurrent ? 'above' : ''}`}
                              >
                                {item.stat}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </Card>
        </>
      )}

      {/* ============== ARMOR MODE ============== */}
      {equipmentMode === 'armor' && (
        <>
          <Card title={t('armorType') || 'ARMOR TYPE'}>
            <div className="armor-type-grid">
              {Object.entries(ARMOR_TYPES).map(([key, type]) => (
                <button
                  key={key}
                  className={`armor-type-btn ${armorType === key ? 'active' : ''}`}
                  onClick={() => { setArmorType(key); setCurrentArmorIdx(0); }}
                >
                  <TypeIcon type={type} className="armor-type-icon" size="large" />
                  <span className="armor-type-name">{t(`armor_${key}`) || type.name}</span>
                </button>
              ))}
            </div>
          </Card>

          <Card title={t('myArmor') || 'MY ARMOR SET'}>
            <div className="equipment-selector">
              <select 
                className="equipment-dropdown"
                value={currentArmorIdx}
                onChange={(e) => setCurrentArmorIdx(parseInt(e.target.value))}
              >
                {armors.map((a, idx) => (
                  <option key={idx} value={idx}>
                    [{a.grade}] {a.name} (P.Def {a.pDef})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="enchant-row">
              <span className="enchant-label">{t('enchantLevel') || 'Enchant'}</span>
              <Stepper value={currentArmorEnchant} onChange={setCurrentArmorEnchant} min={0} max={16} small />
            </div>
            
            <div className="stat-display">
              <span className="stat-label">P.Def (Set)</span>
              <span className="stat-value">
                {currentArmorPDef}
                {currentArmorEnchant > 0 && <span className="enchant-tag">+{currentArmorEnchant}</span>}
              </span>
            </div>
          </Card>

          <Card title={t('compareTo') || 'COMPARES TO'}>
            <div className="equipment-list">
              {armorComparisons.map((comp) => renderComparisonRow(comp, 'P.Def', currentArmorEnchant, currentArmorPDef, {
                expandedItem: expandedArmor,
                setExpandedItem: setExpandedArmor,
                calculateStat: (armor, lvl) => calculateArmorDef(armor.pDef, lvl, armor.grade, armor.pieces),
                currentItem: currentArmor
              }))}
            </div>
            
            <div
              className="enchant-breakdown-toggle"
              onClick={() => setShowArmorBreakdown(!showArmorBreakdown)}
            >
              {showArmorBreakdown ? (t('hideBreakdown') || 'Hide breakdown') : (t('viewAllLevels') || 'View all enchant levels')}
            </div>
            
            {showArmorBreakdown && (() => {
              // Reorder: current armor first, then others
              const orderedArmors = [
                currentArmor,
                ...armors.filter(a => a !== currentArmor)
              ];
              const orderedBreakdown = armorEnchantBreakdown.map(row => ({
                ...row,
                items: [
                  row.items.find(i => i.isCurrent),
                  ...row.items.filter(i => !i.isCurrent)
                ]
              }));
              
              return (
                <div className="enchant-breakdown-scroll">
                  <div className="enchant-breakdown-scroll-x">
                    <div className="enchant-breakdown-table">
                      <div className="breakdown-header">
                        <span className="breakdown-level-col sticky-col">+</span>
                        {orderedArmors.map((a, idx) => (
                          <span key={idx} className={`breakdown-item-col ${a === currentArmor ? 'current sticky-current' : ''}`}>
                            <span className={`grade-mini grade-${a.grade}`}>{a.grade}</span>
                            <span className="breakdown-item-name">{a.name}</span>
                          </span>
                        ))}
                      </div>
                      <div className="breakdown-body">
                        {orderedBreakdown.map((row) => (
                          <div 
                            key={row.level} 
                            className={`breakdown-row ${row.level === currentArmorEnchant ? 'highlight' : ''}`}
                          >
                            <span className="breakdown-level-col sticky-col">+{row.level}</span>
                            {row.items.map((item, idx) => (
                              <span 
                                key={idx} 
                                className={`breakdown-item-col ${item.isCurrent ? 'current sticky-current' : ''} ${item.stat >= currentArmorPDef && !item.isCurrent ? 'above' : ''}`}
                              >
                                {item.stat}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </Card>
        </>
      )}

      {/* ============== JEWELRY MODE ============== */}
      {equipmentMode === 'jewelry' && (
        <>
          {/* Sets vs Individual Toggle */}
          <div className="jewelry-mode-toggle">
            {Object.entries(JEWELRY_MODES).map(([key, mode]) => (
              <button
                key={key}
                className={`jewelry-mode-btn ${jewelryMode === key ? 'active' : ''}`}
                onClick={() => setJewelryMode(key)}
              >
                {t(`jewelry_${key}`) || mode.name}
              </button>
            ))}
          </div>

          {jewelryMode === 'individual' && (
            <Card title={t('jewelryPieceType') || 'PIECE TYPE'}>
              <div className="jewelry-piece-grid">
                {Object.entries(JEWELRY_PIECE_TYPES).map(([key, type]) => (
                  <button
                    key={key}
                    className={`jewelry-piece-btn ${jewelryPieceType === key ? 'active' : ''}`}
                    onClick={() => { setJewelryPieceType(key); setCurrentJewelryPieceIdx(0); }}
                  >
                    <TypeIcon type={type} className="jewelry-piece-icon" />
                    <span className="jewelry-piece-name">{t(`jewelry_${key}`) || type.name}</span>
                  </button>
                ))}
              </div>
            </Card>
          )}

          <Card title={jewelryMode === 'sets' ? (t('myJewelrySet') || 'MY JEWELRY SET') : (t('myJewelryPiece') || 'MY JEWELRY')}>
            <div className="equipment-selector">
              {jewelryMode === 'sets' ? (
                <select 
                  className="equipment-dropdown"
                  value={currentJewelrySetIdx}
                  onChange={(e) => setCurrentJewelrySetIdx(parseInt(e.target.value))}
                >
                  {jewelrySets.map((s, idx) => (
                    <option key={idx} value={idx}>
                      [{s.grade}] {s.name} (M.Def {s.mDef})
                    </option>
                  ))}
                </select>
              ) : (
                <select 
                  className="equipment-dropdown"
                  value={currentJewelryPieceIdx}
                  onChange={(e) => setCurrentJewelryPieceIdx(parseInt(e.target.value))}
                >
                  {jewelryPieces.map((p, idx) => (
                    <option key={idx} value={idx}>
                      {p.isEpic ? '★' : `[${p.grade}]`} {p.name} (M.Def {p.mDef})
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="enchant-row">
              <span className="enchant-label">{t('enchantLevel') || 'Enchant'}</span>
              <Stepper value={currentJewelryEnchant} onChange={setCurrentJewelryEnchant} min={0} max={16} small />
            </div>
            
            <div className="stat-display">
              <span className="stat-label">M.Def {jewelryMode === 'sets' ? '(Set)' : ''}</span>
              <span className="stat-value">
                {currentJewelryMDef}
                {currentJewelryEnchant > 0 && (
                  <span className="enchant-tag">+{currentJewelryEnchant}</span>
                )}
                {jewelryMode === 'individual' && currentJewelryPiece?.isEpic && (
                  <span className="epic-tag">★ Epic</span>
                )}
              </span>
            </div>
          </Card>

          <Card title={t('compareTo') || 'COMPARES TO'}>
            <div className="equipment-list">
              {jewelryComparisons.map((comp) => renderComparisonRow(comp, 'M.Def', currentJewelryEnchant, currentJewelryMDef, {
                expandedItem: expandedJewelry,
                setExpandedItem: setExpandedJewelry,
                calculateStat: jewelryMode === 'sets'
                  ? (set, lvl) => calculateJewelryMDef(set.mDef, lvl, set.grade, set.pieces)
                  : (piece, lvl) => calculateJewelryMDef(piece.mDef, lvl, piece.grade, 1),
                currentItem: jewelryMode === 'sets' ? currentJewelrySet : currentJewelryPiece
              }))}
            </div>
            
            <div
              className="enchant-breakdown-toggle"
              onClick={() => setShowJewelryBreakdown(!showJewelryBreakdown)}
            >
              {showJewelryBreakdown ? (t('hideBreakdown') || 'Hide breakdown') : (t('viewAllLevels') || 'View all enchant levels')}
            </div>
            
            {showJewelryBreakdown && (() => {
              // Reorder: current item first, then others
              const sourceItems = jewelryMode === 'sets' ? jewelrySets : jewelryPieces;
              const currentItem = jewelryMode === 'sets' ? currentJewelrySet : currentJewelryPiece;
              const orderedItems = [
                currentItem,
                ...sourceItems.filter(item => item !== currentItem)
              ];
              const orderedBreakdown = jewelryEnchantBreakdown.map(row => ({
                ...row,
                items: [
                  row.items.find(i => i.isCurrent),
                  ...row.items.filter(i => !i.isCurrent)
                ]
              }));
              
              return (
                <div className="enchant-breakdown-scroll">
                  <div className="enchant-breakdown-scroll-x">
                    <div className="enchant-breakdown-table">
                      <div className="breakdown-header">
                        <span className="breakdown-level-col sticky-col">+</span>
                        {orderedItems.map((item, idx) => (
                          <span 
                            key={idx} 
                            className={`breakdown-item-col ${item === currentItem ? 'current sticky-current' : ''}`}
                          >
                            <span className={`grade-mini ${item.isEpic ? 'grade-epic' : `grade-${item.grade}`}`}>
                              {item.isEpic ? '★' : item.grade}
                            </span>
                            <span className="breakdown-item-name">{item.name}</span>
                          </span>
                        ))}
                      </div>
                      <div className="breakdown-body">
                        {orderedBreakdown.map((row) => (
                          <div 
                            key={row.level} 
                            className={`breakdown-row ${row.level === currentJewelryEnchant ? 'highlight' : ''}`}
                          >
                            <span className="breakdown-level-col sticky-col">+{row.level}</span>
                            {row.items.map((item, idx) => (
                              <span 
                                key={idx} 
                                className={`breakdown-item-col ${item.isCurrent ? 'current sticky-current' : ''} ${item.stat >= currentJewelryMDef && !item.isCurrent ? 'above' : ''}`}
                              >
                                {item.stat}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </Card>
        </>
      )}
    </div>
  );
}
